import React, { useState, useRef, useEffect, useCallback } from "react"
import { connect } from "react-redux"
import Img from "gatsby-image"
import Flickity from "react-flickity-component"
import isEqual from "lodash/isEqual"
import find from "lodash/find"

import VariantSelector from "./variantSelector"
import AddToCartWithQuantitySelector from "./addToCartWithQuantitySelector"
import MobileProductForm from "./mobileProductForm"
import ProductImageSelector from "./productImageSelector"

import { addVariantToCart } from "../../state/shop"
// lib
import { formatPrice, isBrowser, convertToSlug, slugify } from "../../lib"
// hooks
import useDrawer from '../../hooks/useDrawer';

const carouselOptions = {
  draggable: true,
  initialIndex: 0,
  wrapAround: true,
  pageDots: false,
  adaptiveHeight: true,
  selectedAttraction: 0.2,
  friction: 0.8,
  prevNextButtons: false,
}

export const Product = ({ product, shop, add, mobile, collectionSlug, containerRef}) => {



  const {
    options,
    variants,
    variants: [initialVariant],
    priceRange: { minVariantPrice },
  } = product


  const [variant, setVariant] = useState({ ...initialVariant })
  const [sliderRef, setSliderRef] = useState(null)
  const [quantity, setQuantity] = useState(1)
  const [mode, setMode] = useState(0)
  const atcRef = useRef(null)

  const [available, setAvailable] = useState(variant.availableForSale)

  const description = product.descriptionHtml
  const price = formatPrice(minVariantPrice.currencyCode, variant.price, 0)

  const checkAvailability = useCallback(
    productId => {
      shop.client.product.fetch(productId).then(fetchedProduct => {
        const result = fetchedProduct.variants.filter(
          v => v.id === variant.shopifyId
        )
        if (result.length > 0) {
          setAvailable(result[0].available)
        }
      })
    },
    [shop.client.product, variant.shopifyId]
  )

  useEffect(() => {
    checkAvailability(product.shopifyId)
  }, [variant, checkAvailability, product.shopifyId])

  const handleOptionChange = (optionIndex, { target }) => {
    const { value } = target
    const currentOptions = [...variant.selectedOptions]

    currentOptions[optionIndex] = {
      ...currentOptions[optionIndex],
      value,
    }

    const selectedVariant = find(variants, ({ selectedOptions }) =>
      isEqual(currentOptions, selectedOptions)
    )

    setVariant({ ...selectedVariant })
    setImageToSelectedVariant(selectedVariant)
  }

  useEffect(() => {
    const resetModeListener = e => {
      if (mode === 1 && !atcRef.current.contains(e.target)) {
        setMode(0)
      }
    }

    isBrowser() && document.body.addEventListener("click", resetModeListener)
    return () => {
      isBrowser() &&
        document.body.removeEventListener("click", resetModeListener)
    }
  }, [mode])

  const setImageToSelectedVariant = selectedVariant => {
    const selectedVariantImageId = selectedVariant.image.id
    const imageIndex = product.images
      .map(img => img.id)
      .indexOf(selectedVariantImageId)

    if (
      sliderRef &&
      sliderRef.selectedIndex !== imageIndex &&
      variants.length > 1 &&
      imageIndex > -1
    ) {
      sliderRef.select(imageIndex)
    }
  }

  const checkDisabled = (name, value) => {
    const match = find(variants, {
      selectedOptions: [
        {
          name: name,
          value: value,
        },
      ],
    })
    if (match === undefined) return true
    if (match.availableForSale === true) return false
    return true
  }

  const handleAddToCart = () => {
    add(variant.shopifyId, quantity, product.title)
  }


  /// handle proudction change link
  const productRef = useRef()

  const {
    currentProudct,
    changeProudct
  } = useDrawer()

  const proudctSlug = slugify(product.title);

  useEffect(() => {

        const isCurrentProudct = proudctSlug === currentProudct;

        if (isCurrentProudct /*&& isFirst */&& !!containerRef) {
          containerRef.current.scroll({
            top: productRef.current.offsetTop+200,
            left: 0,
            behavior: 'smooth'
          });
        }
    
    }, [currentProudct])
    
    
  const handleProductChange = (drawerSlug, proudctSlug) =>{
    changeProudct(drawerSlug, proudctSlug)
  }


  return (
    <div 
      className={`product`}   
      ref={productRef}
    >
      <div className="product__image-container">
        <Flickity
          className={"product__image-carousel"}
          static={true}
          options={carouselOptions}
          flickityRef={ref => setSliderRef(ref)}
        >
          {product.images.map((image, idx) => (
            <Img
              loading="eager"
              key={idx}
              imgStyle={{
                objectFit: "cover",
              }}
              alt={`${product.title} - Image ${idx + 1}`}
              className="product__carousel-item"
              fluid={image.localFile.childImageSharp.fluid}
            />
          ))}
        </Flickity>
        <div className="product__image-buttons">
          <div
            tabIndex="0"
            role="button"
            aria-label="Previous"
            onClick={() => sliderRef.previous()}
          ></div>
          <div
            tabIndex="0"
            role="button"
            aria-label="Next"
            onClick={() => sliderRef.next()}
          ></div>
        </div>
        <ProductImageSelector
          images={product.images}
          sliderRef={sliderRef ? sliderRef : null}
        />
      </div>
      <div className="product__details">
        <div className="product__details-inner" onClick={()=> handleProductChange(collectionSlug ,proudctSlug)}>
          <h2>{product.title}</h2>
          <h3 className="h2">{price}</h3>
          <div className="product__description--mobile">
            <p dangerouslySetInnerHTML={{ __html: description }} />
          </div>
          <div className="product__variant-selectors">
            {options.map(({ id, name, values }, index) => {
              const selectedOption = variant.selectedOptions.find(
                o => o.name === name
              ).value

              return (
                <VariantSelector
                  key={id}
                  mobile={mobile}
                  name={name}
                  values={values}
                  index={index}
                  selectedOption={selectedOption}
                  checkDisabled={checkDisabled}
                  handleOptionChange={handleOptionChange}
                />
              )
            })}
          </div>
          <div className="product__description--desktop">
            <p dangerouslySetInnerHTML={{ __html: description }} />
          </div>
          <AddToCartWithQuantitySelector
            ref={atcRef}
            quantity={quantity}
            setQuantity={setQuantity}
            handleAddToCart={handleAddToCart}
            available={available}
            loading={shop.loading}
            mode={mode}
            setMode={setMode}
          />
          <MobileProductForm
            quantity={quantity}
            setQuantity={setQuantity}
            handleAddToCart={handleAddToCart}
            available={available}
            loading={shop.loading}
          />
        </div>
        <div className="product__marquee">
          {product.productDetails.map((detail, idx) => (
            <span key={`${detail}-${idx + 1}`}>{detail}</span>
          ))}
          {product.productDetails.map((detail, idx) => (
            <span key={`${detail}-${(idx + 1) * 2}`}>{detail}</span>
          ))}
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = state => ({
  shop: state.shop,
  mobile: state.global.mobile,
})

const mapDispatchToProps = dispatch => ({
  add: (id, qty, title) => dispatch(addVariantToCart(id, qty, title)),
})

export default connect(mapStateToProps, mapDispatchToProps)(Product)
